import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  AppUserRole,
  getGetCurrentUserQueryKey,
  getListAdminUsersQueryKey,
  useGetCurrentUser,
  useListAdminUsers,
  useUpdateAdminUser,
  type AdminUser,
  type UpdateAdminUserRequest
} from '@cdf/api-client-react';
import { SearchIcon, ShieldIcon } from 'lucide-react';

import { useSession } from '@/components/auth/user-provider';
import { PageHeader } from '@/components/layout/page-header';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { UsersIcon } from '@/lib/navigation';

const ROLE_OPTIONS: AppUserRole[] = [
  AppUserRole.admin,
  AppUserRole.teacher,
  AppUserRole.parent,
  AppUserRole.student,
  AppUserRole.pending
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return (first + last).toUpperCase();
}

function roleLabel(role: AppUserRole): string {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'teacher':
      return 'Teacher';
    case 'parent':
      return 'Parent';
    case 'student':
      return 'Student';
    case 'pending':
      return 'Pending';
    default:
      return role;
  }
}

function formatLastSignIn(value: string | null | undefined): string {
  if (!value) return 'Never';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

type PendingChange =
  | { kind: 'role'; user: AdminUser; nextRole: AppUserRole }
  | { kind: 'active'; user: AdminUser; nextActive: boolean };

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const [search, setSearch] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [banner, setBanner] = React.useState<{ tone: 'ok' | 'error'; text: string } | null>(
    null
  );
  const [pending, setPending] = React.useState<PendingChange | null>(null);

  React.useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(search.trim()), 250);
    return () => window.clearTimeout(id);
  }, [search]);

  const listParams = debouncedQ ? { q: debouncedQ } : undefined;
  const { data, isLoading, isError, error, refetch, isFetching } = useListAdminUsers(listParams);
  const signedIn = session.status === 'signedIn';
  const { data: me } = useGetCurrentUser({
    query: {
      queryKey: getGetCurrentUserQueryKey(),
      enabled: signedIn,
      retry: false
    }
  });

  const mutation = useUpdateAdminUser({
    mutation: {
      onMutate: async ({ id, data: body }) => {
        await queryClient.cancelQueries({ queryKey: getListAdminUsersQueryKey(listParams) });
        const key = getListAdminUsersQueryKey(listParams);
        const previous = queryClient.getQueryData<{ users: AdminUser[]; total: number }>(key);

        if (previous) {
          queryClient.setQueryData(key, {
            ...previous,
            users: previous.users.map((u) =>
              u.id === id
                ? {
                    ...u,
                    role: body.role ?? u.role,
                    isActive: body.isActive ?? u.isActive,
                    updatedAt: new Date().toISOString()
                  }
                : u
            )
          });
        }

        return { previous, key };
      },
      onError: (err, _vars, context) => {
        if (context?.previous && context.key) {
          queryClient.setQueryData(context.key, context.previous);
        }
        setBanner({ tone: 'error', text: errorMessage(err) });
      },
      onSuccess: (updated) => {
        setBanner({
          tone: 'ok',
          text: `Updated ${updated.displayName} — ${roleLabel(updated.role)}, ${
            updated.isActive ? 'active' : 'inactive'
          }.`
        });
      },
      onSettled: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getListAdminUsersQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() })
        ]);
      }
    }
  });

  const sessionEmail =
    session.status === 'signedIn' ? session.user.email.trim().toLowerCase() : '';
  const currentUserId = me?.appUserId ?? null;

  const isSelf = React.useCallback(
    (user: AdminUser) => {
      if (currentUserId && user.id === currentUserId) return true;
      if (sessionEmail && user.email?.trim().toLowerCase() === sessionEmail) return true;
      return false;
    },
    [currentUserId, sessionEmail]
  );

  const users = data?.users ?? [];

  function requestRoleChange(user: AdminUser, nextRole: AppUserRole) {
    if (user.role === nextRole || isSelf(user)) return;
    const needsConfirm =
      user.role === 'admin' ||
      nextRole === 'pending' ||
      nextRole === 'parent' ||
      nextRole === 'student';
    if (needsConfirm) {
      setPending({ kind: 'role', user, nextRole });
      return;
    }
    void applyChange(user.id, { role: nextRole });
  }

  function requestActiveChange(user: AdminUser, nextActive: boolean) {
    if (user.isActive === nextActive || isSelf(user)) return;
    if (!nextActive) {
      setPending({ kind: 'active', user, nextActive });
      return;
    }
    void applyChange(user.id, { isActive: true });
  }

  async function applyChange(id: string, body: UpdateAdminUserRequest) {
    setBanner(null);
    try {
      await mutation.mutateAsync({ id, data: body });
    } catch {
      // onError already sets banner
    }
  }

  async function confirmPending() {
    if (!pending) return;
    const change = pending;
    setPending(null);
    if (change.kind === 'role') {
      await applyChange(change.user.id, { role: change.nextRole });
    } else {
      await applyChange(change.user.id, { isActive: change.nextActive });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="User management"
        description="Assign roles and activate accounts for people who have signed in."
        icon={UsersIcon}
      />

      {banner && (
        <div
          role="status"
          className={
            banner.tone === 'ok'
              ? 'border-border bg-muted/40 rounded-md border px-4 py-3 text-sm'
              : 'border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-4 py-3 text-sm'
          }
        >
          {banner.text}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <SearchIcon
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or role…"
            className="pl-9"
            aria-label="Search users"
          />
        </div>
        <p className="text-muted-foreground text-sm tabular-nums">
          {isLoading || isFetching ? 'Loading…' : `${data?.total ?? 0} user${(data?.total ?? 0) === 1 ? '' : 's'}`}
        </p>
      </div>

      <Card className="overflow-hidden border-border/80 bg-card py-0 shadow-sm">
        <Table className="[&_thead]:bg-muted/70 [&_thead_th]:h-12 [&_thead_th]:px-4 [&_thead_th]:text-xs [&_thead_th]:font-semibold [&_thead_th]:uppercase [&_thead_th]:tracking-wider [&_tbody_td]:px-4 [&_tbody_td]:py-4">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last sign-in</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground py-12 text-center">
                  Loading users…
                </TableCell>
              </TableRow>
            )}
            {isError && !isLoading && (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <p className="text-muted-foreground text-sm">{errorMessage(error)}</p>
                    <Button size="sm" variant="outline" onClick={() => void refetch()}>
                      Retry
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground py-12 text-center">
                  {debouncedQ
                    ? 'No users match your search.'
                    : 'No signed-in users yet. Accounts appear here after the first Entra sign-in.'}
                </TableCell>
              </TableRow>
            )}
            {users.map((user) => {
              const self = isSelf(user);
              const busy = mutation.isPending && mutation.variables?.id === user.id;
              return (
                <TableRow key={user.id} className={busy ? 'opacity-70' : undefined}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback className="text-xs">
                          {initialsOf(user.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {user.displayName}
                          {self ? (
                            <span className="text-muted-foreground ml-2 text-xs font-normal">
                              (you)
                            </span>
                          ) : null}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {user.email ?? 'No email'}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Select
                        value={user.role}
                        disabled={self || busy}
                        onValueChange={(value) =>
                          requestRoleChange(user, value as AppUserRole)
                        }
                      >
                        <SelectTrigger className="w-[140px]" aria-label={`Role for ${user.displayName}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role} value={role}>
                              <span className="flex items-center gap-2">
                                {role === 'admin' ? <ShieldIcon className="size-3" /> : null}
                                {roleLabel(role)}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {self ? (
                        <span className="text-muted-foreground sr-only sm:not-sr-only sm:text-xs">
                          Cannot change own role
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={user.isActive}
                        disabled={self || busy}
                        onCheckedChange={(checked) => requestActiveChange(user, checked)}
                        aria-label={`${user.isActive ? 'Deactivate' : 'Activate'} ${user.displayName}`}
                      />
                      <Badge variant={user.isActive ? 'secondary' : 'outline'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm tabular-nums">
                    {formatLastSignIn(user.lastLoginAt)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.kind === 'active'
                ? 'Deactivate account?'
                : 'Change user role?'}
            </DialogTitle>
            <DialogDescription>
              {pending?.kind === 'active' ? (
                <>
                  <strong>{pending.user.displayName}</strong> will no longer be able to use the
                  app until an admin reactivates the account.
                </>
              ) : pending?.kind === 'role' ? (
                <>
                  Change <strong>{pending.user.displayName}</strong> from{' '}
                  <strong>{roleLabel(pending.user.role)}</strong> to{' '}
                  <strong>{roleLabel(pending.nextRole)}</strong>? This takes effect on their next
                  request.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={pending?.kind === 'active' ? 'destructive' : 'default'}
              onClick={() => void confirmPending()}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
